import {
  mutationOptions,
  queryOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { QueryError } from 'src/queries/network.utils'
import { GoogleCalendarIntegrationResponse } from 'src/queries/organizations/calendarIntegrations/calendarIntegrations.models'
import {
  connectGoogleCalendarIntegration,
  disconnectGoogleCalendarIntegration,
  getGoogleCalendarIntegration,
} from 'src/queries/organizations/calendarIntegrations/calendarIntegrations.network'
import { CustomMutationOptions } from 'src/queries/queries.utils'

export const calendarIntegrationQueryKeys = {
  google: (orgId: string | undefined) =>
    ['calendarIntegrations', 'google', orgId] as const,
}

const getGoogleCalendarIntegrationForOrganization = (
  orgId: string | undefined,
) => {
  if (!orgId) {
    throw new Error('Calendar integration organization id is required')
  }

  return getGoogleCalendarIntegration(orgId)
}

export const calendarIntegrationQueries = {
  google: (orgId: string | undefined) =>
    queryOptions({
      queryKey: calendarIntegrationQueryKeys.google(orgId),
      queryFn: () => getGoogleCalendarIntegrationForOrganization(orgId),
      enabled: Boolean(orgId),
      staleTime: 5 * 60 * 1000,
    }),
}

export interface CalendarIntegrationMutationParams {
  orgId: string
}

const requireOrganizationId = (orgId: string) => {
  if (!orgId) {
    throw new Error('Calendar integration organization id is required')
  }

  return orgId
}

export const calendarIntegrationMutations = {
  connectGoogle: () =>
    mutationOptions<
      GoogleCalendarIntegrationResponse,
      QueryError,
      CalendarIntegrationMutationParams
    >({
      mutationFn: ({ orgId }) =>
        connectGoogleCalendarIntegration(requireOrganizationId(orgId)),
    }),
  disconnectGoogle: () =>
    mutationOptions<
      GoogleCalendarIntegrationResponse,
      QueryError,
      CalendarIntegrationMutationParams
    >({
      mutationFn: ({ orgId }) =>
        disconnectGoogleCalendarIntegration(requireOrganizationId(orgId)),
    }),
}

type CalendarIntegrationMutationOptions = CustomMutationOptions<
  GoogleCalendarIntegrationResponse,
  QueryError,
  CalendarIntegrationMutationParams
>

export function useConnectGoogleCalendarIntegration(
  options?: CalendarIntegrationMutationOptions,
) {
  const queryClient = useQueryClient()
  const { onError, onSuccess, ...restOptions } = options ?? {}

  return useMutation({
    ...calendarIntegrationMutations.connectGoogle(),
    onSuccess: (data, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({
        queryKey: calendarIntegrationQueryKeys.google(variables.orgId),
        exact: true,
      })
      onSuccess?.(data, variables, onMutateResult, context)
    },
    onError: (error, variables, onMutateResult, context) => {
      if (error.errorCode === 'CALENDAR_DELEGATION_NOT_READY') {
        queryClient.invalidateQueries({
          queryKey: calendarIntegrationQueryKeys.google(variables.orgId),
          exact: true,
        })
      }
      onError?.(error, variables, onMutateResult, context)
    },
    ...restOptions,
  })
}

export function useDisconnectGoogleCalendarIntegration(
  options?: CalendarIntegrationMutationOptions,
) {
  const queryClient = useQueryClient()
  const { onSuccess, ...restOptions } = options ?? {}

  return useMutation({
    ...calendarIntegrationMutations.disconnectGoogle(),
    onSuccess: (data, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({
        queryKey: calendarIntegrationQueryKeys.google(variables.orgId),
        exact: true,
      })
      onSuccess?.(data, variables, onMutateResult, context)
    },
    ...restOptions,
  })
}
